import React, { useState, useRef, useMemo, useEffect } from "react";
import Grid from "@mui/material/Grid";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination
} from "@mui/material";
import DragAndDropInput from "../../components/DragAndDropInput";
import CustomInput from "../../components/CustomInput";
import { useForm } from "../../hooks/useForm";
import { AddNewEmployeeValidation } from "../../helper/Validation";
import styles from "./style";
import { formatFormDate } from "../../utils/DateFormat";
import { LoadingButton } from "@mui/lab";
import countryList from "react-select-country-list";
import PhoneInput from "react-phone-input-2";
import Modal from "../../components/Modal";
import style from "../SuperAdminPage/style";
import { useNavigate } from "react-router-dom";
import { ContentCopyOutlined, ShareOutlined, ExpandLess, ExpandMore, ArrowRightAlt as ArrowRightAltIcon } from "@mui/icons-material";
import CompanyListSection from "../AccountSettings/CompanyList";
import { normalizePhoneValue } from "../../utils/phone";
import AuthService from "../../api/AuthService";
import PhoneVerificationCard from "../../components/PhoneVerificationCard";
import AutopayToggle from "../../components/AutopayToggle";


const ProfilePage = ({
  registerEmployee,
  userData,
  handleUrl,
  handleNavigate,
  loading,
  imageUploading,
  CountryCode,
  data,
  departmentData,
  handleTransfer,
  planDetails,
  companyList,
  opens,
  setOpens,
  handleDeleteCompany,
  handleDeleteProfileImage,
  showAlert,
}) => {
  const [mobileNumber, setMobileNumber] = useState("")
  const [cCode, setCCode] = useState("")
  const mobileNumberRef = useRef(null);
  const [role] = useState(() => localStorage.getItem("role") || "");
  const [emailTransfer, setEmailTransfer] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isChanged, setIsChanged] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const historyRowsPerPage = 10;
  const billingInfoRef = useRef(null);
  const [showBillingError, setShowBillingError] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchHistory = async () => {
      const currentEmp = userData?._id;
      if (!currentEmp) {
        setHistoryData([]);
        return;
      }
      try {
        const res = await AuthService.getUpdateHistory(currentEmp);
        if (active && res?.success) {
          setHistoryData(res.data || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchHistory();
    return () => { active = false; };
  }, [userData]);

  useEffect(() => {
    if (userData?.personalInfo) {
      const phone = userData.personalInfo.phone || userData.personalInfo.mobile || "";
      const code = String(userData.personalInfo.countryCode || userData.personalInfo.cCode || "");
      const cleanCode = code.replace("+", "");
      const combined = cleanCode ? cleanCode + phone : phone;
      setMobileNumber(combined);
      setCCode(cleanCode);
    }
  }, [userData]);


  const [open, setOpen] = useState(false);


  const validateEmail = (email) => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  }

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmailTransfer(value);

    // Validate email
    if (!validateEmail(value)) {
      setEmailError("Invalid email format");
    } else {
      setEmailError(""); // Clear error if valid
    }
  };


  const fieldMapping = {
    "personalInfo.surName": "lastName",
    "personalInfo.addressLine1": "address1",
    "billingInfo.address1": "address1",
    "personalInfo.addressLine2": "address2",
    "billingInfo.address2": "address2",
    "personalInfo.state": "states",
    "billingInfo.state": "states",
    "personalInfo.zipCode": "zipCode",
    "billingInfo.zipcode": "zipCode",
    "bankInfo.acType": "accountType",
    "bankInfo.bankCode": "ifscCode",
    "bankInfo.taxCode": "personalTaxCode",
    "bankInfo.bank": "bankName",
    "bankInfo.account": "accountNum"
  };

  const pendingChanges = useMemo(() => {
    const changes = {};
    if (userData?.pendingChanges && Array.isArray(userData.pendingChanges)) {
      userData.pendingChanges.forEach(change => {
        if (change.status === "pending") {
          const mappedKey = fieldMapping[change.field] || change.field.split('.').pop();
          changes[mappedKey] = change.newValue;
        }
      });
    }
    return changes;
  }, [userData]);

  const restoreValueRef = useRef(null);

  const onRegister = (data, resetCallback) => {
    registerEmployee(data, userData, resetCallback);
  };
  const options = countryList().getData();

  const handleCopyInviteLink = async () => {
    if (!planDetails?.inviteLink) return;
    try {
      await navigator.clipboard.writeText(planDetails.inviteLink);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 1800);
    } catch (error) {
      setInviteCopied(false);
    }
  };

  const handleShareInviteLink = async () => {
    if (!planDetails?.inviteLink) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Join CabLinks",
          text: "Join my CabLinks network using this link",
          url: planDetails.inviteLink,
        });
        return;
      }
      await handleCopyInviteLink();
    } catch (error) {
      // Ignore share cancellation.
    }
  };

  const handleProfileImageChange = (file) => {
    if (!file) return;

    const localPreviewUrl = URL.createObjectURL(file);
    setDisplayProfileImage(localPreviewUrl);
    setIsChanged(true);

    Promise.resolve(handleUrl(file)).then((uploadedImageUrl) => {
      if (uploadedImageUrl) {
        setDisplayProfileImage(uploadedImageUrl);
        URL.revokeObjectURL(localPreviewUrl);
        return;
      }

      setDisplayProfileImage(updateProfile?.profileImg?.value || "");
      URL.revokeObjectURL(localPreviewUrl);
    });
  };

  const shouldShowCompanyList = role === "Admin";
  const isEmployeeRole = role === "Employee";
  const shouldShowProfilePhotoUpload =
    role === "Admin" || role === "Employee";
  const shouldShowPlanManagement =
    role !== "SuperAdmin" && role !== "Employee";

  const savedSurname = userData?.personalInfo?.surName || "";
  const isSavedSurnameEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(savedSurname);
  const safeSavedSurname = isSavedSurnameEmail ? "" : savedSurname;

  const updateProfile = useMemo(() => ({
    firstName: {
      value: userData?.personalInfo?.firstName || "",
      error: "",
      focus: null,
    },
    lastName: {
      value: safeSavedSurname,
      error: "",
      focus: null,
    },
    nickName: {
      value: userData?.personalInfo?.nickName || "",
      error: "",
      focus: null,
    },
    gender: {
      value: userData?.personalInfo?.gender || "",
      error: "",
      focus: null,
    },
    mobile: {
      value: normalizePhoneValue(
        userData?.personalInfo?.phone ?? userData?.personalInfo?.mobile
      ),
      error: "",
      focus: null,
    },
    password: {
      value: "",
      error: "",
      focus: null,
    },
    confirmPassword: {
      value: "",
      error: "",
      focus: null,
    },
    countryCode: {
      value: userData?.personalInfo?.countryCode || userData?.personalInfo?.cCode || "",
      error: "",
      focus: null,
    },
    jobPosition: {
      value: userData?.personalInfo?.jobPosition || "",
      error: "",
      focus: null,
    },
    department: {
      value: userData?.personalInfo?.department || "",
      error: "",
      focus: null,
    },
    address1: {
      value:
        userData?.billingInfo?.address1 ||
        userData?.personalInfo?.addressLine1 ||
        "",
      error: "",
      focus: null,
    },
    address2: {
      value:
        userData?.billingInfo?.address2 ||
        userData?.personalInfo?.addressLine2 ||
        "",
      error: "",
      focus: null,
    },
    city: {
      value: userData?.billingInfo?.city || userData?.personalInfo?.city || "",
      error: "",
      focus: null,
    },
    states: {
      value:
        userData?.billingInfo?.state ||
        userData?.personalInfo?.states ||
        userData?.personalInfo?.state ||
        "",
      error: "",
      focus: null,
    },
    zipCode: {
      value:
        userData?.billingInfo?.zipcode ||
        userData?.personalInfo?.zipCode ||
        "",
      error: "",
      focus: null,
    },
    country: {
      value:
        userData?.billingInfo?.country ||
        userData?.personalInfo?.country ||
        "",
      error: "",
      focus: null,
    },
    email: {
      value: userData?.personalInfo?.email || "",
      error: "",
      focus: null,
    },
    dob: { value: userData?.personalInfo?.dob || "", error: "", focus: null },
    education: {
      value: userData?.personalInfo?.education || "",
      error: "",
      focus: null,
    },

    profileImg: {
      value: userData?.personalInfo?.image || "",
      error: "",
      focus: null,
    },
    payrollName: {
      value: userData?.salaryInfo?.payrollName || "",
      error: "",
      focus: null,
    },
    status: {
      value: userData?.salaryInfo?.status || "",
      error: "",
      focus: null,
    },
    leaveBalance: {
      value: userData?.salaryInfo?.leaveB || "",
      error: "",
      focus: null,
    },
    passportNum: {
      value: userData?.otherInformation?.passportNo || "",
      error: "",
      focus: null,
    },
    emergencyContact: {
      value: userData?.otherInformation?.emergencyContact || "",
      error: "",
      focus: null,
    },
    workplace: {
      value: userData?.otherInformation?.workplace || "",
      error: "",
      focus: null,
    },
    hourlyRate: {
      value: userData?.salaryInfo?.hRate || "",
      error: "",
      focus: null,
    },
    bankName: { value: userData?.bankInfo?.bank || "", error: "", focus: null },
    accountNum: {
      value: userData?.bankInfo?.account || "",
      error: "",
      focus: null,
    },
    ifscCode: {
      value: userData?.bankInfo?.bankCode || "",
      error: "",
      focus: null,
    },
    accountHolder: {
      value: userData?.bankInfo?.accountHolder || "",
      error: "",
      focus: null,
    },
    personalTaxCode: {
      value: userData?.bankInfo?.taxCode || "",
      error: "",
      focus: null,
    },
    issuedDate: {
      value: userData?.bankInfo?.issuedDate || "",
      error: "",
      focus: null,
    },
    accountType: {
      value: userData?.bankInfo?.acType || "",
      error: "",
      focus: null,
    },

  }), [userData, safeSavedSurname]);
  const [displayProfileImage, setDisplayProfileImage] = useState(
    updateProfile?.profileImg?.value || ""
  );

  useEffect(() => {
    setDisplayProfileImage(updateProfile?.profileImg?.value || "");
  }, [updateProfile]);

  const handleOnChanges = (value, country) => {
    const dialCode = country?.dialCode || "";
    const phoneNumberPart = value.startsWith(dialCode)
      ? value.slice(dialCode.length)
      : value;

    setMobileNumber(value);
    setCCode(dialCode);
    useFormHandleChange("mobile", phoneNumberPart);
    useFormHandleChange("countryCode", "+" + dialCode);
    setIsChanged(true);
  };

  const { state, handleChange: useFormHandleChange, handleSubmit, handleFocus } = useForm(
    updateProfile,
    AddNewEmployeeValidation,
    onRegister
  );
  restoreValueRef.current = useFormHandleChange;
  const handleChange = (name, value, other) => {
    if (updateProfile[name]?.value !== value) {
      setIsChanged(true);
    }
    useFormHandleChange(name, value, other);

  };
  const { firstName, lastName, gender, nickName, mobile, jobPosition, department, address1, address2, city, states, zipCode, country, email, dob,
    education, bankName, accountNum, ifscCode, accountHolder, accountType, issuedDate, password, confirmPassword } = state;



  return (

    <Box sx={styles.pageWrap}>
      {shouldShowCompanyList && (
        <CompanyListSection
          companyList={companyList}
          opens={opens}
          setOpens={setOpens}
          handleDeleteCompany={handleDeleteCompany}
        />
      )}
      <form onSubmit={(e) => handleSubmit(e)} autoComplete="off">
        <Box sx={styles.mainGrid}>
          <Typography sx={styles.heading} variant="h4">
            Profile
          </Typography>
        </Box>
        {localStorage.getItem("role") === "Admin" && (
          <PhoneVerificationCard phone={mobileNumber} />
        )}
        <Grid container spacing={0} sx={styles.sectionGrid}>
          <Grid item xs={12} md={6} lg={6} sx={{ ...styles.equalGridItem, ...styles.columnCell }}>
            <Box sx={styles.inputGrid}>
              <Box sx={styles.greenLine}
              ></Box>
              <Typography sx={styles.subHeading}>
                Personal Information
              </Typography>
            </Box>
            <Grid container spacing={2} sx={styles.equalGridContainer}>
              <Grid item xs={12} sx={styles.sectionCard}>
                <Grid container spacing={1}>
                  {shouldShowProfilePhotoUpload && (

                    <Grid item xs={12} sx={styles.inputGrid}
                    >
                      <DragAndDropInput
                        profileImg={displayProfileImage}
                        handleUrl={handleProfileImageChange}
                        isFrom={"edit"}
                        uploading={!!imageUploading}
                        replaceEditIconWithDelete
                        onDelete={() => {
                          setDisplayProfileImage("");
                          setIsChanged(true);
                          handleDeleteProfileImage();
                        }}
                      />
                      <Typography sx={styles.changeText}>
                        {imageUploading ? "Uploading Profile Photo..." : "Change Profile Photo"}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={styles.inputText}>First Name</InputLabel>
                    <CustomInput
                      defaultValue={updateProfile?.firstName?.value}
                      label=""
                      name="firstName"
                      value={firstName?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      error={firstName?.error}
                      pendingValue={pendingChanges?.firstName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={styles.inputText}>Surname</InputLabel>
                    <CustomInput
                      label=""
                      name="lastName"
                      defaultValue={updateProfile?.lastName?.value}
                      value={lastName?.value}
                      autoComplete="new-password"
                      onChange={handleChange}
                      onFocus={handleFocus}
                      error={lastName?.error}
                      pendingValue={pendingChanges?.lastName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={styles.inputText}>Email</InputLabel>
                    <CustomInput
                      label=""
                      name="email"
                      defaultValue={updateProfile?.email?.value}
                      value={email?.value}
                      autoComplete="email"
                      editable={true}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      error={email?.error}
                      pendingValue={pendingChanges?.email}
                    />
                  </Grid>
                  {!isEmployeeRole && (
                    <Grid item xs={12} sm={6}>
                      <InputLabel sx={styles.inputText}>Phone</InputLabel>
                      <FormControl fullWidth sx={styles.formPhone} >
                        <PhoneInput
                          country={"za"}
                          enableSearch={true}
                          value={normalizePhoneValue(mobileNumber || mobile?.value)}
                          name="mobile"
                          onChange={(value, country) => handleOnChanges(value, country)}
                          onFocus={handleFocus}
                          ref={mobileNumberRef}
                          error={mobile?.error}
                          sx={styles.mobile}
                          inputProps={{ style: { height: "2.6em", borderRadius: "10px", } }}
                        />
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={styles.inputText}>New Password</InputLabel>
                    <CustomInput
                      fullWidth
                      error={password?.error}
                      name="password"
                      type={"password"}
                      autoComplete="new-password"
                      value={password?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      variant="outlined"
                      required
                      altrName="password"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={styles.inputText}>Confirm Password</InputLabel>
                    <CustomInput
                      fullWidth
                      error={confirmPassword?.error}
                      altrName="password"
                      name="confirmPassword"
                      type={"password"}
                      autoComplete="new-password"
                      value={confirmPassword?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      variant="outlined"
                      required
                      matchPassword={password.value}
                    />
                  </Grid>
                  {isEmployeeRole && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Nick Name</InputLabel>
                        <CustomInput
                          label=""
                          name="nickName"
                          defaultValue={updateProfile?.nickName?.value}
                          value={nickName?.value}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          error={nickName?.error}
                          pendingValue={pendingChanges?.nickName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Date Of Birth</InputLabel>
                        <CustomInput
                          label=""
                          name="dob"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          defaultValue={(updateProfile?.dob?.value && formatFormDate(updateProfile?.dob?.value) !== "-") ? formatFormDate(updateProfile?.dob?.value) : ""}
                          value={(dob?.value && formatFormDate(dob.value) !== "-") ? formatFormDate(dob?.value) : ""}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          error={dob?.error}
                          pendingValue={(pendingChanges?.dob && formatFormDate(pendingChanges.dob) !== "-") ? formatFormDate(pendingChanges.dob) : ""}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Gender</InputLabel>
                        <FormControl fullWidth sx={styles.formGrid}>
                          <Select
                            defaultValue={updateProfile?.gender?.value}
                            value={
                              gender.value
                                ? gender?.value
                                : updateProfile?.gender?.value
                            }
                            onChange={(e) => handleChange("gender", e.target.value)}
                            name="gender"
                            onFocus={handleFocus}
                            error={gender?.error}
                            sx={styles.dropDownMenu}
                          >
                            <MenuItem sx={styles.dropDownMenu} value="male">
                              Male
                            </MenuItem>
                            <MenuItem sx={styles.dropDownMenu} value="female">
                              Female
                            </MenuItem>
                            <MenuItem sx={styles.dropDownMenu} value="other">
                              Other
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Mobile</InputLabel>
                        <FormControl fullWidth sx={styles.formPhone} >

                          <PhoneInput
                            country={"za"}
                            enableSearch={true}
                            value={normalizePhoneValue(mobileNumber || mobile?.value)}
                            name="mobile"
                            onChange={(value, country) => handleOnChanges(value, country)}
                            onFocus={handleFocus}
                            ref={mobileNumberRef}
                            error={mobile.error}
                            sx={styles.mobile}
                            inputProps={{ style: { height: "2.6em", borderRadius: "10px", } }}

                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Job Position</InputLabel>
                        <CustomInput
                          label=""
                          name="jobPosition"
                          value={jobPosition?.value}
                          defaultValue={updateProfile?.jobPosition?.value}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          error={jobPosition?.error}
                          pendingValue={pendingChanges?.jobPosition}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Education</InputLabel>
                        <CustomInput
                          label=""
                          name="education"
                          defaultValue={updateProfile?.education.value}
                          value={education?.value}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          error={education.error}
                          pendingValue={pendingChanges?.education}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* --- MOVED BILLING INFO --- */}
            <Box sx={{ ...styles.inputGrid, mt: 3 }} ref={billingInfoRef}>
              <Box sx={styles.greenLine}></Box>
              <Typography sx={styles.subHeading}>
                Billing Information
                {showBillingError && (
                  <span style={{ color: "#d32f2f", fontSize: "14px", marginLeft: "10px", fontWeight: "normal" }}>
                    * Required to manage plan
                  </span>
                )}
              </Typography>
            </Box>
            <Grid container spacing={2} sx={styles.equalGridContainer}>
              <Grid item xs={12} sx={styles.sectionCard}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <InputLabel sx={styles.inputText}>Address 1</InputLabel>
                    <CustomInput
                      label=""
                      name="address1"
                      defaultValue={updateProfile?.address1?.value}
                      value={address1?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      error={address1?.error}
                      pendingValue={pendingChanges?.address1}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel sx={styles.inputText}>Address 2</InputLabel>
                    <CustomInput
                      label=""
                      name="address2"
                      defaultValue={updateProfile?.address2?.value}
                      value={address2?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      error={address2?.error}
                      pendingValue={pendingChanges?.address2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={styles.inputText}>City</InputLabel>
                    <CustomInput
                      label=""
                      name="city"
                      defaultValue={updateProfile?.city?.value}
                      value={city?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      error={city?.error}
                      pendingValue={pendingChanges?.city}
                    />
                  </Grid>
                  {role !== "Employee" && (
                    <Grid item xs={12} sm={6}>
                      <InputLabel sx={styles.inputText}>State/Province</InputLabel>
                      <CustomInput
                        label=""
                        name="states"
                        defaultValue={updateProfile?.states?.value}
                        value={states?.value}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        error={states?.error}
                        pendingValue={pendingChanges?.states}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={styles.inputText}>Zipcode</InputLabel>
                    <CustomInput
                      label=""
                      name="zipCode"
                      defaultValue={updateProfile?.zipCode?.value}
                      value={zipCode?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      error={zipCode?.error}
                      type="number"
                      pendingValue={pendingChanges?.zipCode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={styles.inputText}>Country</InputLabel>
                    <FormControl fullWidth sx={{ ...styles.uniformInputHeight, mt: "0.5em" }}>
                      <Select
                        defaultValue={updateProfile?.country?.value}
                        value={country?.value}
                        displayEmpty
                        renderValue={(selected) => (selected ? selected : "Country")}
                        onChange={(event) => {
                          handleChange("country", event.target.value);
                          CountryCode(event.target.value);
                        }}
                        name="country"
                        sx={styles.dropDownMenu}
                        onFocus={handleFocus}
                        error={country.error}
                      >
                        <MenuItem value="" disabled>
                          Country
                        </MenuItem>
                        {options?.map((country, index) => (
                          <MenuItem key={index} sx={styles.dropDownMenu} value={country.label}>
                            {country.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* --- MOVED BANK INFO --- */}
            {isEmployeeRole && (
              <>
                <Box sx={{ ...styles.inputGrid, mt: 3 }}>
                  <Box sx={styles.greenLine}></Box>
                  <Typography sx={styles.subHeading}>Bank Information</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sx={styles.card}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Bank Name</InputLabel>
                        <CustomInput
                          fullWidth
                          label=""
                          name="bankName"
                          defaultValue={updateProfile?.bankName.value}
                          value={bankName?.value}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          error={bankName?.error}
                          pendingValue={pendingChanges?.bankName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Account</InputLabel>
                        <CustomInput
                          fullWidth
                          label=""
                          name="accountNum"
                          defaultValue={updateProfile?.accountNum.value}
                          value={accountNum?.value}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          error={accountNum?.error}
                          type="number"
                          pendingValue={pendingChanges?.accountNum}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>{data?.bankCode || "Branch Code"}</InputLabel>
                        <CustomInput
                          fullWidth
                          label=""
                          name="ifscCode"
                          value={ifscCode?.value}
                          defaultValue={updateProfile?.ifscCode.value}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          error={ifscCode?.error}
                          pendingValue={pendingChanges?.ifscCode}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Account Holder</InputLabel>
                        <CustomInput
                          fullWidth
                          label=""
                          name="accountHolder"
                          defaultValue={updateProfile?.accountHolder.value}
                          onChange={handleChange}
                          value={accountHolder?.value}
                          onFocus={handleFocus}
                          error={accountHolder?.error}
                          pendingValue={pendingChanges?.accountHolder}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={styles.inputText}>Account Type</InputLabel>
                        <CustomInput
                          fullWidth
                          label=""
                          name="accountType"
                          defaultValue={updateProfile?.accountType.value}
                          onChange={handleChange}
                          value={accountType?.value}
                          onFocus={handleFocus}
                          error={accountType?.error}
                          pendingValue={pendingChanges?.accountType}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>

          <Grid item xs={12} md={6} lg={6} sx={{ ...styles.equalGridItem, ...styles.columnCell }}>
            {isEmployeeRole && (
              <Box sx={{ position: 'sticky', top: '20px' }}>
                <Box sx={styles.inputGrid}>
                  <Box sx={styles.greenLine} style={{ backgroundColor: "#8b5cf6" }}></Box>
                  <Typography sx={styles.subHeading}>Request History</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sx={{ ...styles.card, pl: 2, height: "auto", maxHeight: "calc(100vh - 100px)", overflowY: "auto" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <Typography sx={{ fontWeight: "bold" }}>All Requests</Typography>
                      <Box sx={{ backgroundColor: "#fef3c7", color: "#d97706", fontSize: "12px", padding: "2px 8px", borderRadius: "10px" }}>
                        {historyData?.filter(c => c.status === "pending").length || 0} Pending
                      </Box>
                    </Box>

                    {(historyData || [])
                      .slice((historyPage - 1) * historyRowsPerPage, historyPage * historyRowsPerPage)
                      .map((req, idx) => {
                        const fieldName = req.field ? req.field.split('.').pop() : "Field";
                        const formatValue = (val) => {
                          if (val === undefined || val === null || val === "") return "N/A";
                          if (Array.isArray(val)) return val.join(", ");
                          return String(val);
                        };

                        return (
                          <Box key={idx} sx={{ mb: 2, pb: 2, borderBottom: "1px solid #eaeaea" }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: "bold", mb: 1, color: "#374151" }}>
                              {fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography sx={{ fontSize: "12px", color: "#9ca3af", textDecoration: req.oldValue === undefined || req.oldValue === null || req.oldValue === "" ? "none" : "line-through", wordBreak: "break-all" }}>
                                {formatValue(req.oldValue)}
                              </Typography>
                              <ArrowRightAltIcon sx={{ color: "#d1d5db", fontSize: "16px" }} />
                              <Typography sx={{ fontSize: "12px", color: "#1fc157", fontWeight: "500", wordBreak: "break-all" }}>
                                {formatValue(req.newValue)}
                              </Typography>
                              {req.status && (
                                <Box sx={{ marginLeft: "auto", backgroundColor: req.status === "rejected" ? "#fee2e2" : req.status === "accepted" ? "#d1fae5" : "#fef3c7", color: req.status === "rejected" ? "#dc2626" : req.status === "accepted" ? "#059669" : "#d97706", fontSize: "10px", padding: "2px 6px", borderRadius: "8px", textTransform: "capitalize" }}>
                                  {req.status}
                                </Box>
                              )}
                            </Box>
                            {req.comment && (
                              <Typography sx={{ fontSize: "11px", color: "#6b7280", mt: 0.5, fontStyle: "italic" }}>
                                Note: {req.comment}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}

                    {historyData && historyData.length > historyRowsPerPage && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pb: 2 }}>
                        <Pagination
                          count={Math.ceil(historyData.length / historyRowsPerPage)}
                          page={historyPage}
                          onChange={(e, val) => setHistoryPage(val)}
                          color="primary"
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}



          </Grid>

        </Grid>
        <Box sx={styles.formActionRow}>
          <LoadingButton
            loading={loading || imageUploading}
            variant="contained"
            type="submit"
            color="success"
            sx={styles.successBtn}
            disabled={!isChanged || imageUploading}

          >
            {isEmployeeRole ? "Send Request" : "Save Changes"}
          </LoadingButton>
          <Button
            variant="contained"
            onClick={handleNavigate}
            color="success"
            sx={styles.cancelBtn}
          >
            Cancel
          </Button>
        </Box>

        <Grid container spacing={0} sx={styles.sectionGrid}>
          {!isEmployeeRole && (
          <Grid item xs={12} md={6} lg={6} sx={{ ...styles.equalGridItem, ...styles.columnCell }}>
            <Box sx={styles.inputGrid}>
              <Box sx={styles.greenLine}
              ></Box>
              <Typography sx={styles.subHeading}>
                Transfer Account
              </Typography>
            </Box>
            <Grid container spacing={2} sx={styles.equalGridContainer}>
              <Grid item xs={12} sx={styles.sectionCard}>
                <Grid container spacing={1}>

                  <Grid item xs={12} sm={12}>
                    <InputLabel sx={styles.inputText}>Email</InputLabel>

                    <TextField
                      name="emailTransfer"
                      value={emailTransfer}
                      sx={{ ...style.emailTransfer, ...styles.uniformInputHeight }}
                      onChange={handleEmailChange}
                      error={Boolean(emailError)} // Mark field as error if validation fails
                      helperText={emailError} // Display error message
                    />
                  </Grid>
                </Grid>
                <LoadingButton
                  variant="contained"
                  onClick={() => setOpen(true)}
                  color="primary"
                  disabled={!emailTransfer || Boolean(emailError)}
                  sx={{ ...styles.submitButton, ...styles.transferButtonFit }}
                >
                  Transfer
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
          )}

          {shouldShowPlanManagement && (
            <Grid item xs={12} md={6} lg={6} sx={{ ...styles.equalGridItem, ...styles.columnCell }}>
              <Box sx={styles.inputGrid}>
                <Box sx={styles.greenLine}></Box>
                <Typography sx={styles.subHeading}>Plan Management</Typography>
              </Box>
              <Grid container spacing={2} sx={styles.equalGridContainer}>
                <Grid item xs={12} sx={styles.sectionCard}>
                  <Box sx={styles.planInfoRow}>
                    <Typography sx={styles.planInfoLabel}>Current Plan</Typography>
                    <Typography sx={styles.planInfoValue}>
                      {planDetails?.planName || "Not Available"}
                    </Typography>
                  </Box>
                  <Box sx={styles.planInfoRow}>
                    <Typography sx={styles.planInfoLabel}>Plan Expires On</Typography>
                    <Typography sx={styles.planInfoValue}>
                      {planDetails?.expiryDate || "Not Available"}
                    </Typography>
                  </Box>
                  <Box sx={styles.planInfoRow}>
                    <Typography sx={styles.planInfoLabel}>Plan Rate</Typography>
                    <Typography sx={styles.planInfoValue}>
                      {planDetails?.planRate || "Not Available"}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <AutopayToggle
                      subscriptionId={planDetails?.subscriptionId}
                      initialAutoRenew={planDetails?.autoRenew}
                      autoRenewStatus={planDetails?.autoRenewStatus}
                      hasPaymentAuthorization={planDetails?.hasPaymentAuthorization}
                    />
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="text"
                      size="small"
                      sx={{ textTransform: "none" }}
                      onClick={() => navigate("/dashboard/topups")}
                    >
                      View topups &amp; usage
                    </Button>
                  </Box>
                  <Box sx={styles.transactionsWrap}>
                    <Box sx={styles.transactionsHeaderRow} onClick={() => setShowTransactions((prev) => !prev)}>
                      <Typography sx={styles.transactionsHeading}>Previous Transactions</Typography>
                      <Button
                        variant="text"
                        size="small"
                        sx={styles.transactionsToggleBtn}
                        endIcon={showTransactions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      >
                        {showTransactions ? "Hide" : "Show"}
                      </Button>
                    </Box>
                    {showTransactions && (
                      (planDetails?.previousTransactions || []).length ? (
                        <TableContainer sx={styles.transactionTableWrap}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={styles.transactionHeadCell}>Plan</TableCell>
                                <TableCell sx={styles.transactionHeadCell}>Date</TableCell>
                                <TableCell sx={styles.transactionHeadCell}>Status</TableCell>
                                <TableCell sx={styles.transactionHeadCell} align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(planDetails?.previousTransactions || []).map((tx) => (
                                <TableRow key={tx.id}>
                                  <TableCell sx={styles.transactionBodyCell}>{tx.planName}</TableCell>
                                  <TableCell sx={styles.transactionBodyCell}>{tx.date}</TableCell>
                                  <TableCell sx={styles.transactionBodyCell}>{tx.status}</TableCell>
                                  <TableCell sx={styles.transactionBodyCell} align="right">{tx.amount}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography sx={styles.transactionMeta}>No previous transactions</Typography>
                      )
                    )}
                  </Box>

                  <Box sx={styles.mncStandardCard}>
                    <Typography sx={styles.mncHeading}>Invite Friends</Typography>
                    <Typography sx={styles.mncText}>
                      {planDetails?.inviteLink || "Invite link not available"}
                    </Typography>
                    <Box sx={styles.inviteActions}>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={styles.inviteBtn}
                        onClick={handleCopyInviteLink}
                        disabled={!planDetails?.inviteLink}
                        startIcon={<ContentCopyOutlined fontSize="small" />}
                      >
                        {inviteCopied ? "Copied" : "Copy"}
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        sx={styles.invitePrimaryBtn}
                        onClick={handleShareInviteLink}
                        disabled={!planDetails?.inviteLink}
                        startIcon={<ShareOutlined fontSize="small" />}
                      >
                        Share
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={styles.planActionsRow}>
                    {/* <LoadingButton
                      variant="outlined"
                      color="success"
                      sx={styles.downgradePlanBtn}
                      onClick={() => navigate("/plan", { state: { fromDowngrade: true } })}
                    >
                      Downgrade Plan
                    </LoadingButton> */}
                    <LoadingButton
                      variant="contained"
                      color="success"
                      sx={styles.upgradePlanBtn}
                      onClick={() => {
                        const isBillingComplete =
                          address1?.value &&
                          city?.value &&
                          states?.value &&
                          zipCode?.value &&
                          country?.value;

                        if (!isBillingComplete) {
                          showAlert("Please complete your Billing Information", "error");
                          setShowBillingError(true);
                          if (billingInfoRef.current) {
                            billingInfoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        } else {
                          setShowBillingError(false);
                          navigate("/planManagement");
                        }
                      }}
                    >
                      Manage Plan
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}

        </Grid>
      </form>
      <Modal open={open} setOpen={setOpen} deletetText={"Transfer"} cancelText={"Cancel"} title={"Transfer Account"} handleDelete={() => { handleTransfer(emailTransfer), handleLogout() }}>
        <Box>
          <Typography>Are you sure you want to Transfer your Account?</Typography>

        </Box>
      </Modal>
    </Box>
  );
};

export default ProfilePage;
